/**
 * AnnouncementModal.js
 * 공지사항 알림 모달 컴포넌트
 * 사용자가 읽지 않은 공지사항을 확인할 수 있는 모달
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
  Chip,
} from '@mui/material';
import { X, Bell } from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import apiConfig from '../config/api';

const API_BASE_URL = apiConfig.V1;

const AnnouncementModal = ({ open, onClose, onRead }) => {
  const { colors, isDark } = useCustomTheme();
  const [announcements, setAnnouncements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (open) {
      fetchActiveAnnouncements();
      setCurrentIndex(0);
    }
  }, [open]);

  const fetchActiveAnnouncements = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/announcements/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(response.data || []);
    } catch (error) {
      console.error('공지사항 조회 실패:', error);
      setAnnouncements([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsRead = async (announcementId) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/announcements/${announcementId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // 읽음 처리된 공지사항 제거
      setAnnouncements(prev => prev.filter(a => a.announcement_id !== announcementId));
      
      // 읽음 처리 콜백 호출
      if (onRead) {
        onRead();
      }
      
      // 모든 공지사항을 읽었으면 모달 닫기
      if (announcements.length === 1) {
        onClose();
      } else {
        // 다음 공지사항으로 이동 (현재 인덱스 유지, 이미 제거되었으므로)
        if (currentIndex >= announcements.length - 1) {
          setCurrentIndex(0);
        }
      }
    } catch (error) {
      console.error('읽음 처리 실패:', error);
      alert(`읽음 처리 실패: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleNext = () => {
    if (currentIndex < announcements.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleClose = () => {
    setCurrentIndex(0);
    onClose();
  };

  if (!open) return null;

  const currentAnnouncement = announcements[currentIndex];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: isDark ? colors.background : '#fff',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Bell size={20} style={{ color: colors.accent }} />
          <Typography variant="h6">공지사항</Typography>
          {announcements.length > 1 && (
            <Chip
              label={`${currentIndex + 1} / ${announcements.length}`}
              size="small"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        <IconButton onClick={handleClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ pt: 3 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography>로딩 중...</Typography>
          </Box>
        ) : currentAnnouncement ? (
          <Box>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: colors.primaryText,
              }}
            >
              {currentAnnouncement.title}
            </Typography>
            
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`시작일: ${currentAnnouncement.start_date}`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`종료일: ${currentAnnouncement.end_date}`}
                size="small"
                variant="outlined"
              />
            </Box>
            
            <Typography
              variant="body1"
              sx={{
                whiteSpace: 'pre-wrap',
                color: colors.secondaryText,
                lineHeight: 1.8,
                minHeight: 100,
              }}
            >
              {currentAnnouncement.message}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Bell size={48} style={{ color: colors.secondaryText, opacity: 0.5, marginBottom: 16 }} />
            <Typography variant="body1" sx={{ color: colors.secondaryText }}>
              확인할 공지사항이 없습니다.
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        {announcements.length > 1 && (
          <Box sx={{ display: 'flex', gap: 1, mr: 'auto' }}>
            <Button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              variant="outlined"
              size="small"
            >
              이전
            </Button>
            <Button
              onClick={handleNext}
              disabled={currentIndex === announcements.length - 1}
              variant="outlined"
              size="small"
            >
              다음
            </Button>
          </Box>
        )}
        
        <Button onClick={handleClose} variant="outlined">
          닫기
        </Button>
        
        {currentAnnouncement && (
          <Button
            onClick={() => handleMarkAsRead(currentAnnouncement.announcement_id)}
            variant="contained"
            sx={{ backgroundColor: colors.accent }}
          >
            확인
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AnnouncementModal;

